import React, { useState, useEffect, useContext } from "react";
import {
  getDocs,
  query,
  where,
  collection,
  doc,
  updateDoc,
  orderBy,
  getDoc
} from "firebase/firestore";
import {
  Card,
  Button,
  Collapse,
  Badge,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import {
  CalendarDays,
  IndianRupee,
  Users,
  ChevronDown,
  ChevronUp,
  MapPin,
  CircleCheckBig,
  CircleX,
  BanknoteX,
  CalendarCheck2,
  Bus,
  Car,
  Info,
  InfoIcon,
} from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import { db } from "../FireBase";
import "../css/OrderDetail.css";
import Lottie from "lottie-react";
import noBookings from "../assets/noBookings.json";
import toast, { Toaster } from 'react-hot-toast';
import { Link } from "react-router-dom";

const SightseeingBookingList = () => {
  const { currentUser } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    const fetchSightseeingBookings = async () => {
      if (!currentUser) return;
      try {
        const q = query(
          collection(db, "slightSeeing_booking"),
          where("userId", "==", currentUser.cusmid),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setBookings(data);
      } catch (error) {
        console.error("Error fetching sightseeing bookings:", error);
      }
    };
    fetchSightseeingBookings();
    fetchCancellationPolicy();
  }, [currentUser]);

  const fetchCancellationPolicy = async () => {
    try {
      const ref = doc(db, 'settings', 'cancellation');
      const snap = await getDoc(ref);
      if (snap.exists()) setCancellationPolicy(snap.data());
    } catch (err) {
      console.error('Error fetching cancellation settings:', err);
    }
  };

    const getRefundPercentage = (order) => {
      console.log(order)
    if (!order || !order.date) return 0;
    if (!cancellationPolicy?.sightseeings?.enabled) return 0;
      // console.log(order.date)
    const rules = cancellationPolicy.sightseeings.rules || [];
    if (rules.length === 0) return 0;

    const formatDate = (ddmmyyyy) => {
      const [day, month, year] = ddmmyyyy.split("/");
      return `${year}-${month}-${day}`;
    };

    const startDate = new Date(formatDate(order.date));
    const today = new Date();
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffDays = Math.ceil((startDate - todayNormalized) / (1000 * 60 * 60 * 24));

    const sortedRules = [...rules].sort((a, b) => b.days - a.days);
    const matchingRule = sortedRules.find(rule => diffDays >= rule.days);

    if (matchingRule) return Number(matchingRule.refund);

    const lastRule = sortedRules[sortedRules.length - 1];
    return Number(lastRule.refund);
  };

    const handleCancel = async () => {
    if (!selectedOrder) return;
    setIsCancelling(true);
    try {
      const order = bookings.find(o => o.id === selectedOrder.id);
      if (!order) return;

      const refundPercent = getRefundPercentage(order);
      const refundAmount = ((order.price || 0) * refundPercent) / 100;

      const ref = doc(db, 'slightSeeing_booking', selectedOrder.id);

      await updateDoc(ref, {
        status: 'cancelled',
        refundPercent,
        refundAmount,
      });

      setBookings(prev =>
        prev.map(o =>
          o.id === selectedOrder.id
            ? { ...o, status: 'cancelled', refundPercent, refundAmount }
            : o
        )
      );
      
      // ⭐ Call cancellation API (single API handles FCM + email + admin email)
      await fetch(
        "https://us-central1-kabili.cloudfunctions.net/api/send-cancellation-email",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId: order.orderId,
            refundAmount,
            refundPercent,
          }),
        }
      );

      toast.success(`Booking cancelled. Refund: ₹${refundAmount} (${refundPercent}%)`);

    } catch (err) {
      console.error(err);
      toast.error("Error cancelling booking");
    } finally {
      setShowConfirmModal(false);
      setIsCancelling(false);
    }
  };

    const parseBookingDate = (d) => {
    if (!d) return null;

    // Firestore Timestamp
    if (typeof d === "object" && d.toDate) {
      return d.toDate();
    }

    // Already JS Date
    if (d instanceof Date) {
      return d;
    }

    // DD/MM/YYYY string
    if (typeof d === "string" && d.includes("/")) {
      const [day, month, year] = d.split("/");
      return new Date(`${year}-${month}-${day}`);
    }

    // Timestamp number
    if (typeof d === "number") {
      return new Date(d);
    }

    return null;
  };

  const toggleCollapse = (index) =>
    setOpenIndex(openIndex === index ? null : index);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "booking-confirmed":
        return <CalendarCheck2 className="me-2 text-success" size={22} />;
      case "cancelled":
        return <CircleX className="me-2 text-danger" size={22} />;
      case "payment-pending":
        return <BanknoteX className="me-2 text-warning" size={22} />;
      case "completed":
        return <CircleCheckBig className="me-2 text-info" size={22} />;
      default:
        return <CalendarCheck2 className="me-2 text-secondary" size={22} />;
    }
  };

  if (bookings.length === 0) {
    return (
      <div
        className="empty-order-container"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div className="lottie-wrapper mb-3" style={{ width: 250 }}>
          <Lottie animationData={noBookings} loop={true} />
        </div>
        <p className="empty-order-text">No sightseeing bookings yet!</p>
      </div>
    );
  }

  return (
    <div>
        <Toaster position="top-center" />
      {bookings.map((booking, index) => (
        <Card key={booking.id} className="mb-4 border-0 shadow-sm rounded-4">
          <Card.Header className="bg-light d-flex justify-content-between align-items-center rounded-top-4">
            <div>
              <h5 className="mb-0 d-flex align-items-center" style={{ color: "#54b435" }}>
                <Bus className="me-2" /> {booking.packageName}
              </h5>
              <small className="text-muted">
                <CalendarDays size={16} className="me-1" />
                Booking ID: {booking.orderId}
              </small>
            </div>
            {getStatusIcon(booking.status)}
          </Card.Header>

          <Card.Body>
            <Row>
              <Col md={6}>
                <p>
                  <CalendarDays size={18} className="me-2 text-primary" /> Date:{" "}
                 <strong>{parseBookingDate(booking.date)?.toLocaleDateString() || "N/A"}</strong>
                </p>
                  <p>
                  <Car size={18} className="me-2 text-danger" /> Vehicle:{" "}
                 <strong>{booking.vehicleDetails.name}(Seats:{booking.vehicleDetails.capacity})</strong>
                </p>

              
              </Col>

              <Col md={6}>
                <p>
                  <MapPin size={18} className="me-2 text-success" /> Pickup:{" "}
                  <strong>{booking.pickup}</strong>
                </p>
                <p>
                  <MapPin size={18} className="me-2 text-danger" /> Dropoff:{" "}
                  <strong>{booking.dropoff}</strong>
                </p>
               
              </Col>
            </Row>

            <Button
              size="sm"
              className="mt-3 btn-own-2"
              onClick={() => toggleCollapse(index)}
            >
              {openIndex === index ? (
                <ChevronUp className="me-1" />
              ) : (
                <ChevronDown className="me-1" />
              )}
              {openIndex === index ? "Hide Details" : "View Details"}
            </Button>

            <Collapse in={openIndex === index}>
              <div className="mt-4">
                <h6>Guest Details</h6>
                <ul>
                  
                      {booking.name} ({booking.phone})
                    
                  
                </ul>

                <hr />
                <h6>Payment Info</h6>
                <p>Payment Method: {booking.paymentMethod || "N/A"}</p>
                <p>
                  <strong>Total Paid: ₹{booking.price}</strong>
                </p>
                 <p>
                                    <strong>Status: </strong>
                                    <Badge 
                                      bg={
                                        booking.status === "booking-confirmed" ? "success" :
                                        booking.status === "payment-pending"   ? "warning" :
                                        booking.status === "cancelled"         ? "danger"  :
                                        booking.status === "started"        ? "secondary" :
                                        booking.status === "completed"       ? "secondary" :
                                        "dark"
                                      }
                                    >
                                      {booking.status}
                                    </Badge>
                                  </p>
              {/* ⭐ CANCELLED SUMMARY BLOCK */}
                {booking.status === "cancelled" && (
                  <div className="mt-3 p-3 " style={{backgroundColor:'#ffe6e6',borderRadius:'15px'}}>
                    <h6 className="text-danger">Cancellation Details</h6>

                    <p className="mb-1">
                      <strong>Refund Percentage:</strong> {booking.refundPercent || 0}%
                    </p>

                    <p className="mb-1">
                      <strong>Refund Amount:</strong> ₹{booking.refundAmount || 0}
                    </p>
                    <p style={{fontSize:'12px'}}><InfoIcon size={15} color="red"/> Refund will be initiated in 3 business days. For any refund-related queries, please contact us.</p>
                   

                   
                  </div>
                )}
                 {booking.status !== 'cancelled' && cancellationPolicy?.sightseeings?.enabled && (
                    <>
                      <div className="d-flex flex-column align-items-center mt-3">

                        <p className="text-muted mb-2">
                          {getRefundPercentage(booking) > 0
                            ? `If you cancel now, you will receive a ${getRefundPercentage(booking)}% refund.`
                            : `Cancellation not refundable.`}
                        </p>

                        <Button
                          className="w-100 mt-1 btn-own-danger"
                          onClick={() => {
                            setSelectedOrder(booking);
                            setShowConfirmModal(true);
                          }}
                        >
                          Cancel Booking
                        </Button>
                         <br/>
                            <Link to="/cancellation-policy" className="text-decoration-none text-primary mb-2" >
                            <Info className="me-1" size={16} /> Read Cancellation Policy
                            </Link>
                      </div>
                    </>
                  )}

                  {booking.status !== 'cancelled' && !cancellationPolicy?.sightseeings?.enabled && (
                    <p className="text-muted mt-3">
                      <em>Cancellation not available for this booking.</em>
                    </p>
                  )}
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      ))}

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel this sightseeing booking?
             <strong>Refund:</strong> {selectedOrder ? getRefundPercentage(selectedOrder) : 0}%
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            No
          </Button>
           <Button variant="danger" onClick={handleCancel} disabled={isCancelling}> 
              {isCancelling ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Cancelling...
                </>
              ) : (
                "Yes, Cancel"
              )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SightseeingBookingList;
